import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import '@resistapp/client/components/gene-selection-grid.css';
import { theme } from '@resistapp/client/components/shared/theme';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { isProviderAdminUser, isResistomapAdminUser } from '@resistapp/client/utils/access';
import {
  AdminGenePanel,
  AdminGenePanelAssaysResponse,
  AdminGenePanelPatchRequest,
  AdminGenePanelPatchResponse,
  AdminGenePanelSelectionPatchRequest,
  AdminGenePanelSelectionPatchResponse,
  AdminGenePanelsResponse,
  OrgSamplingPointChoicesResponse,
} from '@resistapp/common/api-types';
import { L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target } from '@resistapp/common/friendly';
import type { DefaultGenePanelDefinition, DefaultGenePanelKey } from '@resistapp/common/gene-panels';
import {
  buildDefaultGenePanelDefinitions,
  DefaultGenePanelDefinitionError,
  findConfigurationIdByCounts,
  findDefaultGenePanelByAssays,
  GenePanelConfigurationId,
  genePanelConfigurationIds,
  getGenePanelConfiguration,
  isGenePanelConfigurationId,
  sortAssaysBySheetOrder,
  sortAssaysNumerically,
} from '@resistapp/common/gene-panels';
import { GenePanelState, LabWorkbookStatus, OrganisationType } from '@resistapp/common/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CellValueChangedEvent,
  ColDef,
  FilterChangedEvent,
  GridReadyEvent,
  ICellRendererParams,
  PasteStartEvent,
  RowSelectionOptions,
  ValueFormatterParams,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGet } from '../../hooks/api';
import { getAuthHeaders, resistomapApiClient } from '../../utils/fetch';
import { isGeneTemplateReadOnly } from '../lab-view/status-utils';

interface SelectionOption {
  value: string;
  label: string;
}

type SelectionValue = string;

type GeneSelectionRow = {
  assay: string;
  gene: string;
  targetAntibioticMajor: string;
  hasStandardCurve: boolean;
  genePackage1: boolean;
  genePackage2: boolean;
  genePackage21: boolean;
  genePackage3: boolean;
  forwardPrimer: string | null;
  reversePrimer: string | null;
  deprecated: boolean;
  softLaunch: boolean;
  selected: boolean;
};

type GeneSelectionGridRow = {
  assay: string;
  gene: string;
  target: string;
  arg10: string;
  arg20: string;
  assayStandardCurve: string;
  arg21: string;
  oneHealth96: string;
  forwardPrimer: string;
  reversePrimer: string;
  softLaunch: boolean;
  selected: boolean;
};

type GeneSelectionClipboardField = Exclude<keyof GeneSelectionGridRow, 'selected' | 'softLaunch'>;

type GeneSelectionClipboardColumn = {
  field: GeneSelectionClipboardField;
  header: string;
};

type SelectionCellContext = {
  onToggle: (assay: string) => void;
  isReadOnlySelection: boolean;
  disableInteractions: boolean;
  canSelectSoftLaunchedAssays: boolean;
};

type SelectionValueSetterParams = {
  newValue: unknown;
  oldValue: unknown;
  data?: GeneSelectionGridRow;
};

const genePanelSelectionClipboardType = 'resistapp-gene-panel-selection-v1' as const;
const forwardPrimerColumnHeader = 'Forward primer 5′→3′';
const reversePrimerColumnHeader = 'Reverse primer 5′→3′';

const assayClipboardColumns = [
  { field: 'assay', header: 'Assay' },
  { field: 'gene', header: 'Assay target' },
  { field: 'target', header: 'Target Group' },
  { field: 'arg10', header: 'ARG 1.0 Panel' },
  { field: 'arg20', header: 'ARG 2.0 Panel' },
  { field: 'assayStandardCurve', header: 'AY std. curve' },
  { field: 'arg21', header: 'ARG 2.1' },
  { field: 'oneHealth96', header: 'One Health 96' },
  { field: 'forwardPrimer', header: forwardPrimerColumnHeader },
  { field: 'reversePrimer', header: reversePrimerColumnHeader },
] satisfies GeneSelectionClipboardColumn[];

const allAssayClipboardColumnsWithoutPrimers = assayClipboardColumns.filter(
  column => column.field !== 'forwardPrimer' && column.field !== 'reversePrimer',
);

function normalizeTsvCell(value: unknown): string {
  return String(value ?? '')
    .replace(/\t/g, ' ')
    .replace(/\r?\n/g, ' ');
}

function buildGeneSelectionTsv(
  rows: readonly GeneSelectionGridRow[],
  columns: readonly GeneSelectionClipboardColumn[],
): string {
  const headerRow = columns.map(column => column.header);
  const dataRows = rows.map(row => columns.map(column => normalizeTsvCell(row[column.field])));
  return [headerRow, ...dataRows].map(cells => cells.join('\t')).join('\n');
}

type GenePanelSelectionClipboardRowV1 = {
  assay: string;
  selected: boolean;
};

type GenePanelSelectionClipboardV1 = {
  type: typeof genePanelSelectionClipboardType;
  rows: GenePanelSelectionClipboardRowV1[];
};

function parseGenePanelSelectionClipboardV1(text: string): GenePanelSelectionClipboardV1 | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const candidate = parsed as Record<string, unknown>;
  if (candidate.type !== genePanelSelectionClipboardType) {
    return null;
  }
  if (!Array.isArray(candidate.rows)) {
    return null;
  }

  const normalizedRows = candidate.rows.map(row => {
    if (!row || typeof row !== 'object') {
      return null;
    }
    const rowCandidate = row as Record<string, unknown>;
    if (typeof rowCandidate.assay !== 'string' || typeof rowCandidate.selected !== 'boolean') {
      return null;
    }
    return { assay: rowCandidate.assay, selected: rowCandidate.selected } satisfies GenePanelSelectionClipboardRowV1;
  });
  const rows = normalizedRows.filter((row): row is GenePanelSelectionClipboardRowV1 => row != null);
  if (rows.length !== normalizedRows.length) {
    return null;
  }
  return { type: genePanelSelectionClipboardType, rows } satisfies GenePanelSelectionClipboardV1;
}

function parseSelectionCellValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === '') {
    return false;
  }
  if (['true', '1', 'yes', 'y', '✓', 'checked', 'x'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', 'unchecked'].includes(normalized)) {
    return false;
  }
  return null;
}

function formatGenePanelStateLabel(state: GenePanelState): string {
  switch (state) {
    case GenePanelState.VALID:
      return 'Valid';
    case GenePanelState.INVALID:
      return 'Invalid';
    case GenePanelState.LOCKED:
      return 'Locked';
    default:
      return state;
  }
}

function genePanelStateColorScheme(state: GenePanelState): string {
  switch (state) {
    case GenePanelState.VALID:
      return 'green';
    case GenePanelState.INVALID:
      return 'orange';
    case GenePanelState.LOCKED:
      return 'purple';
    default:
      return 'gray';
  }
}

type GeneSelectionSheetProps = {
  organisationId: number | undefined;
  organisationIdParam: string | undefined;
  invalidOrganisationId: boolean;
  isEmbedded?: boolean;
  linkedPanelId?: number | null;
  onLinkPanel?: (panelId: number | null) => Promise<void>;
  isLinkingPanel?: boolean;
  labWorkbookStatus?: LabWorkbookStatus | null;
  allowGeneTemplateEdits?: boolean;
};

function getSelectionPayload(value: string): string {
  const separatorIndex = value.indexOf(':');
  return separatorIndex >= 0 ? value.slice(separatorIndex + 1) : '';
}

function SelectionCellRenderer({
  context,
  data,
}: ICellRendererParams<GeneSelectionGridRow, boolean, SelectionCellContext>) {
  if (!data) {
    return null;
  }
  const disabled = isSelectionCellDisabled(data, context);
  const handleChange = () => {
    if (disabled) {
      return;
    }
    context.onToggle(data.assay);
  };
  return <Checkbox isChecked={Boolean(data.selected)} isDisabled={disabled} onChange={handleChange} />;
}

function isSelectionCellDisabled(row: GeneSelectionGridRow, context: SelectionCellContext): boolean {
  return (
    context.disableInteractions ||
    context.isReadOnlySelection ||
    row.assay === 'AY1' ||
    (!context.canSelectSoftLaunchedAssays && row.softLaunch)
  );
}

type CustomNameValidationResult =
  | { valid: true; trimmed: string }
  | { valid: false; trimmed: string; reason: 'empty' | 'duplicate' };

const emptyGenePanels: AdminGenePanel[] = [];

export function GeneSelectionSheet({
  organisationId,
  organisationIdParam,
  invalidOrganisationId,
  isEmbedded = false,
  linkedPanelId = null,
  onLinkPanel,
  isLinkingPanel = false,
  labWorkbookStatus = null,
  allowGeneTemplateEdits = true,
}: GeneSelectionSheetProps) {
  const toast = useToast();
  const { user } = useUser();
  const hasOrganisationId = Number.isFinite(organisationId ?? NaN);
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<SelectionValue>('');
  const [customNameInput, setCustomNameInput] = useState('');
  const [customConfigurationId, setCustomConfigurationId] = useState<GenePanelConfigurationId>('96:18');
  const [optimisticAssays, setOptimisticAssays] = useState<Set<string> | null>(null);
  const [isCustomDraftDirty, setIsCustomDraftDirty] = useState(false);
  const [customModePanelId, setCustomModePanelId] = useState<number | null>(null);
  const lastRequestedPanelIdRef = useRef<number | null>(null);
  const selectionVersionOverridesRef = useRef<Map<number, number>>(new Map());
  const [isCommissioning, setIsCommissioning] = useState(false);
  const [customNameError, setCustomNameError] = useState<string | null>(null);
  const clipboardPasteChangesRef = useRef<Map<string, boolean> | null>(null);
  const selectedExistingPanelId = useMemo(() => {
    if (!selection.startsWith('existing:')) {
      return null;
    }
    const id = Number(getSelectionPayload(selection));
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [selection]);

  const {
    data: panelData,
    isLoading: panelsLoading,
    error: panelsError,
  } = useGet<AdminGenePanelsResponse>(
    hasOrganisationId && organisationId != null ? `admin/organisation-panels/${organisationId}` : '',
    {
      enabled: hasOrganisationId,
      // Preserve AG Grid scroll on tab focus by avoiding automatic refetches
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: assaysData,
    isLoading: assaysLoading,
    error: assaysError,
  } = useGet<AdminGenePanelAssaysResponse>('admin/gene-panels/assays', { refetchOnWindowFocus: false });

  const patchPath = hasOrganisationId && organisationId != null ? `admin/organisation-panels/${organisationId}` : null;
  const panelQueryKey = useMemo(() => (patchPath ? [patchPath] : null), [patchPath]);

  const updatePanelCache = useCallback(
    (panel: AdminGenePanel) => {
      if (!panelQueryKey) {
        return;
      }
      const normalizeTimestamp = (value: unknown): string => {
        if (typeof value === 'string') {
          return value;
        }
        if (value instanceof Date) {
          return value.toISOString();
        }
        if (typeof value === 'number') {
          return new Date(value).toISOString();
        }
        return '';
      };
      queryClient.setQueryData<AdminGenePanelsResponse>(panelQueryKey, previous => {
        if (!previous) {
          return previous;
        }
        const existingIndex = previous.panels.findIndex(existing => existing.id === panel.id);
        const nextPanels =
          existingIndex >= 0
            ? previous.panels.map(existing => (existing.id === panel.id ? panel : existing))
            : [...previous.panels, panel].sort((a, b) =>
                normalizeTimestamp(a.createdAt).localeCompare(normalizeTimestamp(b.createdAt)),
              );
        return { ...previous, panels: nextPanels } satisfies AdminGenePanelsResponse;
      });

      // Keep OrganisationSamples cache in sync so Sample sheet shows up-to-date state/name.
      if (organisationId) {
        const orgSamplesKey = [`/admin/org-sampling-point-choices/${organisationId}`];
        queryClient.setQueryData<OrgSamplingPointChoicesResponse>(orgSamplesKey, previous => {
          if (!previous) {
            return previous;
          }
          const nextGenePanels = previous.genePanels.map(p =>
            p.id === panel.id
              ? { id: panel.id, organisationId: panel.organisationId, name: panel.name, state: panel.state }
              : p,
          );
          return { ...previous, genePanels: nextGenePanels } satisfies OrgSamplingPointChoicesResponse;
        });
      }
    },
    [organisationId, panelQueryKey, queryClient],
  );

  const recordSelectionVersion = useCallback((panel: AdminGenePanel) => {
    selectionVersionOverridesRef.current.set(panel.id, panel.selectionVersion);
  }, []);

  const ensureSelectionVersion = useCallback((panel: AdminGenePanel) => {
    const current = selectionVersionOverridesRef.current.get(panel.id);
    if (current == null || panel.selectionVersion > current) {
      selectionVersionOverridesRef.current.set(panel.id, panel.selectionVersion);
    }
  }, []);

  const getSelectionVersion = useCallback(
    (panel: AdminGenePanel) => selectionVersionOverridesRef.current.get(panel.id) ?? panel.selectionVersion,
    [],
  );

  const commissionMutation = useMutation<AdminGenePanelPatchResponse, Error, AdminGenePanelPatchRequest>({
    mutationFn: async body => {
      if (!patchPath) {
        throw new Error('Organisation is required before saving a gene panel.');
      }
      const response = await resistomapApiClient.patch<AdminGenePanelPatchResponse>(patchPath, body, {
        headers: getAuthHeaders('application/json'),
      });
      return response.data;
    },
    onSuccess: panel => {
      updatePanelCache(panel);
      recordSelectionVersion(panel);
    },
  });

  const selectionMutation = useMutation<
    AdminGenePanelSelectionPatchResponse,
    AxiosError<{ panel?: AdminGenePanel }>,
    { panelId: number; body: AdminGenePanelSelectionPatchRequest }
  >({
    mutationFn: async ({ panelId, body }) => {
      const response = await resistomapApiClient.patch<AdminGenePanelSelectionPatchResponse>(
        `/admin/gene-panels/${panelId}/selection`,
        body,
        {
          headers: getAuthHeaders('application/json'),
        },
      );
      return response.data;
    },
    onSuccess: ({ panel }) => {
      updatePanelCache(panel);
      recordSelectionVersion(panel);
      setOptimisticAssays(null);
    },
    onError: error => {
      const serverPanel = error.response?.data.panel;
      if (serverPanel) {
        updatePanelCache(serverPanel);
        recordSelectionVersion(serverPanel);
        setOptimisticAssays(null);
      }
    },
  });

  const panelList = panelData?.panels ?? [];
  const panels = panelList.length > 0 ? panelList : emptyGenePanels;
  const panelsById = useMemo(() => new Map(panels.map(panel => [panel.id, panel])), [panels]);

  // Organisation info available via panelData if needed for future UI
  const assayList = assaysData?.assays ?? null;
  const isResistomapAdmin = isResistomapAdminUser(user);
  const canSelectSoftLaunchedAssays = (user?.organisations ?? []).some(
    organisation => organisation.type === OrganisationType.RESISTOMAP,
  );
  const isCustomerOnly =
    (user?.organisations.length ?? 0) > 0 &&
    (user?.organisations ?? []).every(organisation => organisation.type === OrganisationType.CUSTOMER);
  const canCopySelectionsToAnotherWorkbook = (isResistomapAdmin || isProviderAdminUser(user)) && !isCustomerOnly;

  const defaultGenePanelDefinitionsResult = useMemo(() => {
    if (!assayList) {
      return { definitions: [] as DefaultGenePanelDefinition[], error: null as Error | null };
    }
    try {
      return { definitions: buildDefaultGenePanelDefinitions(assayList), error: null };
    } catch (error) {
      if (error instanceof DefaultGenePanelDefinitionError) {
        console.error(error);
        const message = `${error.meta.name} expected ${error.expectedAssays} assays but got ${error.actualAssays}.`;
        return { definitions: [] as DefaultGenePanelDefinition[], error: new Error(message) };
      }
      const resolvedError =
        error instanceof Error ? error : new Error('Failed to build default gene panel definitions');
      console.error(resolvedError);
      return { definitions: [] as DefaultGenePanelDefinition[], error: resolvedError };
    }
  }, [assayList]);

  const defaultGenePanelDefinitions = defaultGenePanelDefinitionsResult.definitions;
  const defaultGenePanelDefinitionsError = defaultGenePanelDefinitionsResult.error;

  const validateCustomName = useCallback(
    (raw: string, options?: { excludeId?: number | null }): CustomNameValidationResult => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setCustomNameError('Name is required.');
        return { valid: false, trimmed, reason: 'empty' };
      }
      const normalized = trimmed.toLowerCase();
      const conflict = panels.some(
        panel => panel.id !== (options?.excludeId ?? null) && panel.name.trim().toLowerCase() === normalized,
      );
      if (conflict) {
        setCustomNameError('A panel with this name already exists.');
        return { valid: false, trimmed, reason: 'duplicate' };
      }
      setCustomNameError(null);
      return { valid: true, trimmed };
    },
    [panels],
  );

  const selectedExistingPanel = useMemo(() => {
    if (selectedExistingPanelId == null) {
      return null;
    }
    return panelsById.get(selectedExistingPanelId) ?? null;
  }, [panelsById, selectedExistingPanelId]);

  useEffect(() => {
    if (!selectedExistingPanel) {
      return;
    }
    setOptimisticAssays(null);
    const inferredId = findConfigurationIdByCounts(selectedExistingPanel.genes, selectedExistingPanel.samples);
    const defaultDefinition = findDefaultGenePanelByAssays(defaultGenePanelDefinitions, selectedExistingPanel.assays);
    const isCustomPanel = !defaultDefinition || selectedExistingPanel.state !== GenePanelState.VALID;
    if (isCustomPanel) {
      setCustomModePanelId(selectedExistingPanel.id);
      if (!isCustomDraftDirty) {
        setCustomNameInput(selectedExistingPanel.name);
        if (inferredId) {
          setCustomConfigurationId(inferredId);
        }
        setCustomNameError(null);
      }
    } else {
      setCustomModePanelId(null);
      setIsCustomDraftDirty(false);
      setCustomNameError(null);
    }
  }, [defaultGenePanelDefinitions, isCustomDraftDirty, selectedExistingPanel]);

  useEffect(() => {
    if (!selectedExistingPanel) {
      return;
    }
    ensureSelectionVersion(selectedExistingPanel);
  }, [ensureSelectionVersion, selectedExistingPanel]);

  useEffect(() => {
    if (linkedPanelId == null) {
      return;
    }
    if (selection.startsWith('custom:') || selection.startsWith('default:')) {
      return;
    }
    if (!panelsById.has(linkedPanelId)) {
      return;
    }
    if (selectedExistingPanelId === linkedPanelId) {
      return;
    }
    setSelection(`existing:${linkedPanelId}`);
  }, [linkedPanelId, panelsById, selectedExistingPanelId, selection]);

  useEffect(() => {
    if (!onLinkPanel) {
      return;
    }
    if (selectedExistingPanelId == null) {
      lastRequestedPanelIdRef.current = null;
      return;
    }
    if (linkedPanelId === selectedExistingPanelId) {
      lastRequestedPanelIdRef.current = selectedExistingPanelId;
      return;
    }
    if (lastRequestedPanelIdRef.current === selectedExistingPanelId) {
      return;
    }
    lastRequestedPanelIdRef.current = selectedExistingPanelId;
    void onLinkPanel(selectedExistingPanelId).catch(() => {
      lastRequestedPanelIdRef.current = null;
    });
  }, [linkedPanelId, onLinkPanel, selectedExistingPanelId]);

  const panelsBySignature = useMemo(() => {
    return new Map(panels.map(panel => [sortAssaysNumerically(panel.assays).join('|'), panel] as const));
  }, [panels]);

  const defaultPanelMatches = useMemo(() => {
    const map = new Map<string, AdminGenePanel>();
    defaultGenePanelDefinitions.forEach(def => {
      const signature = sortAssaysNumerically(def.assays).join('|');
      const match = panelsBySignature.get(signature);
      if (match) {
        map.set(def.key, match);
      }
    });
    return map;
  }, [defaultGenePanelDefinitions, panelsBySignature]);

  const selectedDefaultDefinition = useMemo(() => {
    if (!selection.startsWith('default:')) {
      return undefined;
    }
    const key = getSelectionPayload(selection);
    return defaultGenePanelDefinitions.find(def => def.key === key);
  }, [defaultGenePanelDefinitions, selection]);

  const selectionCustomConfigId = useMemo<GenePanelConfigurationId | null>(() => {
    if (!selection.startsWith('custom:')) {
      return null;
    }
    const candidate = getSelectionPayload(selection);
    return isGenePanelConfigurationId(candidate) ? candidate : null;
  }, [selection]);
  const isCustomCreationSelected = selectionCustomConfigId != null;

  const selectionOptions = useMemo<SelectionOption[]>(() => {
    const options: SelectionOption[] = [{ value: '', label: 'Select a panel option' }];
    if (panels.length > 0) {
      options.push(
        ...panels.map(panel => ({
          value: `existing:${panel.id}`,
          label: `Use ${panel.name} (${formatGenePanelStateLabel(panel.state)})`,
        })),
      );
    }
    const defaultOptions = defaultGenePanelDefinitions
      .filter(def => !defaultPanelMatches.has(def.key))
      .map(def => ({ value: `default:${def.key}`, label: `Commission ${def.name}` }));
    options.push(...defaultOptions);
    const customOptions = genePanelConfigurationIds.map(id => {
      const cfg = getGenePanelConfiguration(id);
      return {
        value: `custom:${id}`,
        label: `Create custom ${cfg.genes} genes / ${cfg.samples} samples`,
      } satisfies SelectionOption;
    });
    options.push(...customOptions);
    return options;
  }, [defaultGenePanelDefinitions, defaultPanelMatches, panels]);

  const isCustomPanelActive =
    isCustomCreationSelected ||
    (customModePanelId != null && selectedExistingPanel != null && selectedExistingPanel.id === customModePanelId);

  const selectedPanelState = selectedExistingPanel?.state ?? null;
  const canCopySelectedAssayPrimers = isResistomapAdmin || selectedPanelState === GenePanelState.LOCKED;

  const currentConfigurationId: GenePanelConfigurationId | null = (() => {
    if (isCustomPanelActive) {
      return customConfigurationId;
    }
    if (selectedExistingPanel) {
      return findConfigurationIdByCounts(selectedExistingPanel.genes, selectedExistingPanel.samples) ?? null;
    }
    if (selectedDefaultDefinition) {
      return selectedDefaultDefinition.configurationId;
    }
    return null;
  })();

  const currentConfiguration = currentConfigurationId ? getGenePanelConfiguration(currentConfigurationId) : null;

  const currentPanelName = (() => {
    if (isCustomPanelActive) {
      return customNameInput;
    }
    if (selectedExistingPanel) {
      return selectedExistingPanel.name;
    }
    return selectedDefaultDefinition?.name ?? '';
  })();

  const selectedAssaySet = useMemo(() => {
    if (optimisticAssays) {
      return optimisticAssays;
    }
    if (selectedExistingPanel) {
      return new Set(selectedExistingPanel.assays);
    }
    if (selectedDefaultDefinition) {
      return new Set(selectedDefaultDefinition.assays);
    }
    if (isCustomPanelActive) {
      return new Set<string>(['AY1']);
    }
    return new Set<string>();
  }, [isCustomPanelActive, optimisticAssays, selectedDefaultDefinition, selectedExistingPanel]);

  // Compute match against defaults for UI cues; keep stable and unconditional.
  // Note: matching against defaults for the selected existing panel is handled separately

  const matchingDefaultForSelectedPanel = useMemo(() => {
    if (!selectedExistingPanel) {
      return undefined;
    }
    const sortedSelected = sortAssaysNumerically(selectedExistingPanel.assays);
    return defaultGenePanelDefinitions.find(def => {
      const sortedDef = sortAssaysNumerically(def.assays);
      return (
        sortedSelected.length === sortedDef.length && sortedSelected.every((assay, index) => assay === sortedDef[index])
      );
    });
  }, [defaultGenePanelDefinitions, selectedExistingPanel]);

  const expectedGeneCount = useMemo(() => {
    if (isCustomPanelActive) {
      return currentConfiguration?.genes ?? null;
    }
    if (selectedExistingPanel) {
      return selectedExistingPanel.assays.length;
    }
    if (selectedDefaultDefinition) {
      return selectedDefaultDefinition.assays.length;
    }
    return null;
  }, [currentConfiguration?.genes, isCustomPanelActive, selectedDefaultDefinition, selectedExistingPanel]);

  const showMatchesDefaultInBadge = useMemo(() => {
    if (!selectedExistingPanel || !matchingDefaultForSelectedPanel) {
      return false;
    }
    return (
      matchingDefaultForSelectedPanel.name.trim().toLowerCase() !== selectedExistingPanel.name.trim().toLowerCase()
    );
  }, [matchingDefaultForSelectedPanel, selectedExistingPanel]);

  const selectedCount = selectedAssaySet.size;
  const hasOnlyMandatoryAssay = useMemo(() => {
    if (selectedAssaySet.size === 0) {
      return true;
    }
    if (selectedAssaySet.size === 1 && selectedAssaySet.has('AY1')) {
      return true;
    }
    // Guard in case AY1 isn't present for some reason
    return Array.from(selectedAssaySet).every(a => a === 'AY1');
  }, [selectedAssaySet]);
  const selectionStatus: 'tooFew' | 'exact' | 'tooMany' | null = useMemo(() => {
    if (expectedGeneCount == null) {
      return null;
    }
    if (selectedCount < expectedGeneCount) {
      return 'tooFew';
    }
    if (selectedCount === expectedGeneCount) {
      return 'exact';
    }
    return 'tooMany';
  }, [expectedGeneCount, selectedCount]);

  const selectionBadgeColorScheme = selectionStatus === 'exact' ? 'green' : selectionStatus == null ? 'gray' : 'yellow';
  const selectionBadgeLabel =
    expectedGeneCount == null
      ? `${selectedCount} assays selected`
      : `${selectedCount}/${expectedGeneCount} assays selected`;

  const isDefaultTemplateSelected = Boolean(selectedDefaultDefinition);
  const isExistingDefaultPanelReadOnly = Boolean(
    selectedExistingPanel &&
      matchingDefaultForSelectedPanel &&
      selectedExistingPanel.state === GenePanelState.VALID &&
      !isCustomPanelActive,
  );
  const isWorkbookReadOnly = isGeneTemplateReadOnly(labWorkbookStatus);
  // Removed status badge from lab page headers; no friendly status needed here.

  // Disable interactions broadly when mutating, locked, default template selected, or workbook is read-only.
  // Important: do NOT disable when creating a new custom panel (no selectedExistingPanel yet).
  // Previously `!selectedExistingPanel` caused the name field to be disabled during creation.
  const disableInteractions =
    selectionMutation.isPending ||
    commissionMutation.isPending ||
    isCommissioning ||
    (!selectedExistingPanel && !isCustomPanelActive) ||
    !assayList ||
    assayList.length === 0 ||
    selectedExistingPanel?.state === GenePanelState.LOCKED ||
    isDefaultTemplateSelected ||
    isExistingDefaultPanelReadOnly ||
    isWorkbookReadOnly;
  const isReadOnlySelection = isWorkbookReadOnly || selectedExistingPanel?.state === GenePanelState.LOCKED;
  const canPasteSelectionClipboard =
    allowGeneTemplateEdits && Boolean(selectedExistingPanel) && !disableInteractions && !isReadOnlySelection;

  const friendlyTargetByKey = useMemo(() => {
    return Object.values(L2Targets).reduce<Record<string, string>>((acc, target) => {
      acc[target] = friendlyL2Target(target);
      return acc;
    }, {});
  }, []);

  const assayByAy = useMemo(() => {
    if (!assayList) {
      return new Map<string, AdminGenePanelAssaysResponse['assays'][number]>();
    }
    return new Map(assayList.map(row => [row.assay, row] as const));
  }, [assayList]);
  const allAssays = useMemo(() => assayList?.map(row => row.assay) ?? [], [assayList]);
  const isAssaySelectionBlocked = useCallback(
    (assay: string) => Boolean(assayByAy.get(assay)?.softLaunch && !canSelectSoftLaunchedAssays),
    [assayByAy, canSelectSoftLaunchedAssays],
  );

  const visibleRows: GeneSelectionRow[] = useMemo(() => {
    if (!assayByAy.size) {
      return [];
    }
    const sortedAssays = sortAssaysBySheetOrder(Array.from(assayByAy.keys()));
    const set = selectedAssaySet;

    return sortedAssays
      .map(assay => assayByAy.get(assay))
      .filter((row): row is AdminGenePanelAssaysResponse['assays'][number] => Boolean(row))
      .filter(row => !row.deprecated || set.has(row.assay))
      .map(row => ({
        ...row,
        selected: set.has(row.assay),
      }));
  }, [assayByAy, selectedAssaySet]);

  const rowData = useMemo((): GeneSelectionGridRow[] => {
    return visibleRows.map(row => ({
      assay: row.assay,
      gene: row.gene,
      target: friendlyTargetByKey[row.targetAntibioticMajor] ?? row.targetAntibioticMajor,
      arg10: row.genePackage1 ? '✓' : '',
      arg20: row.genePackage2 ? '✓' : '',
      assayStandardCurve: row.hasStandardCurve ? '✓' : '',
      arg21: row.genePackage21 ? '✓' : '',
      oneHealth96: row.genePackage3 ? '✓' : '',
      forwardPrimer: row.forwardPrimer ?? '',
      reversePrimer: row.reversePrimer ?? '',
      softLaunch: row.softLaunch,
      selected: selectedAssaySet.has(row.assay),
    }));
  }, [friendlyTargetByKey, selectedAssaySet, visibleRows]);
  const selectedRows = useMemo(() => rowData.filter(row => row.selected), [rowData]);

  const defaultColDef = useMemo<ColDef<GeneSelectionGridRow>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
      cellClass: 'gene-selection-cell',
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  const booleanColumnDef = useMemo<Partial<ColDef<GeneSelectionGridRow>>>(
    () => ({
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['✓', ''],
        valueFormatter: (params: { value: string | null | undefined }) => (params.value ? '✓' : 'Not included'),
      },
      width: 140,
      cellClass: 'gene-selection-cell gene-selection-cell-boolean',
    }),
    [],
  );

  const commissionDefaultPanel = useCallback(
    async (definition: DefaultGenePanelDefinition) => {
      if (isWorkbookReadOnly) {
        return;
      }
      try {
        setIsCommissioning(true);
        const panel = await commissionMutation.mutateAsync({
          name: definition.name,
          configurationId: definition.configurationId,
          assays: Array.from(definition.assays),
        });
        setSelection(`existing:${panel.id}`);
        setCustomModePanelId(null);
        setOptimisticAssays(null);
        toast({ title: 'Panel ready', description: `${definition.name} is available for use.`, status: 'success' });
        if (onLinkPanel) {
          void onLinkPanel(panel.id);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to commission panel';
        toast({ title: 'Could not commission panel', description: message, status: 'error' });
      } finally {
        setIsCommissioning(false);
      }
    },
    [commissionMutation, isWorkbookReadOnly, onLinkPanel, toast],
  );

  const handleApplyDefaultTemplate = useCallback(
    (key: DefaultGenePanelKey) => {
      const panel = selectedExistingPanel;
      const definition = defaultGenePanelDefinitions.find(def => def.key === key);
      if (!panel || !definition || disableInteractions) {
        return;
      }
      // Default templates may include deprecated assays for legacy support (e.g. ARG 2.1 includes AY315).
      // When "starting with" a template for a new editable custom panel, omit deprecated assays so users
      // must explicitly pick modern replacements to reach the expected assay count.
      const targetAssays = Array.from(definition.assays).filter(assay => {
        const meta = assayByAy.get(assay);
        return meta ? !meta.deprecated : true;
      });
      const additions = targetAssays.filter(assay => !panel.assays.includes(assay));
      const removals = panel.assays.filter(assay => !targetAssays.includes(assay));
      const baseVersion = getSelectionVersion(panel);
      setOptimisticAssays(new Set(targetAssays));
      void selectionMutation
        .mutateAsync({
          panelId: panel.id,
          body: {
            baseSelectionVersion: baseVersion,
            addAssays: additions,
            removeAssays: removals,
          },
        })
        .catch(error => {
          const message = error instanceof Error ? error.message : 'Failed to apply template';
          toast({ title: 'Template apply failed', description: message, status: 'error' });
        });
    },
    [
      assayByAy,
      defaultGenePanelDefinitions,
      disableInteractions,
      getSelectionVersion,
      selectedExistingPanel,
      selectionMutation,
      toast,
    ],
  );

  const ensureCustomPanel = useCallback(
    async (nameOverride?: string): Promise<boolean> => {
      if (!allowGeneTemplateEdits) {
        return false;
      }
      if (disableInteractions) {
        return false;
      }
      if (!selectionCustomConfigId) {
        toast({
          title: 'Choose configuration',
          description: 'Select a custom panel configuration first.',
          status: 'error',
        });
        return false;
      }

      const configurationId = selectionCustomConfigId;
      if (configurationId !== customConfigurationId) {
        setCustomConfigurationId(configurationId);
      }

      const validation = validateCustomName(nameOverride ?? customNameInput);
      if (!validation.valid) {
        const reason = validation.reason;
        toast({
          title: reason === 'duplicate' ? 'Name already used' : 'Panel name required',
          status: 'error',
        });
        return false;
      }

      const trimmed = validation.trimmed;

      try {
        setIsCommissioning(true);
        const panel = await commissionMutation.mutateAsync({
          name: trimmed,
          configurationId,
          assays: Array.from(selectedAssaySet),
        });
        setSelection(`existing:${panel.id}`);
        setCustomModePanelId(panel.id);
        setOptimisticAssays(null);
        setCustomNameInput(trimmed);
        toast({ title: 'Custom panel created', description: `${trimmed} is ready to edit.`, status: 'success' });
        if (onLinkPanel) {
          void onLinkPanel(panel.id);
        }
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create custom panel';
        toast({ title: 'Creation failed', description: message, status: 'error' });
        return false;
      } finally {
        setIsCommissioning(false);
      }
    },
    [
      allowGeneTemplateEdits,
      commissionMutation,
      customConfigurationId,
      customNameInput,
      disableInteractions,
      onLinkPanel,
      selectedAssaySet,
      selectionCustomConfigId,
      setSelection,
      toast,
      validateCustomName,
    ],
  );

  const handleCustomNameBlur = useCallback(async () => {
    if (!allowGeneTemplateEdits) {
      return;
    }
    if (!isCustomPanelActive) {
      return;
    }

    if (selection.startsWith('custom:')) {
      await ensureCustomPanel();
      return;
    }

    const panel = selectedExistingPanel;
    if (!panel) {
      return;
    }

    const validation = validateCustomName(customNameInput, { excludeId: panel.id });
    if (!validation.valid) {
      const reason = validation.reason;
      toast({
        title: reason === 'duplicate' ? 'Name already used' : 'Panel name required',
        status: 'error',
      });
      setCustomNameInput(panel.name);
      setCustomNameError(null);
      return;
    }

    const trimmed = validation.trimmed;
    if (panel.name === trimmed) {
      return;
    }

    if (panel.state === GenePanelState.LOCKED) {
      setCustomNameInput(panel.name);
      setCustomNameError(null);
      return;
    }

    try {
      await commissionMutation.mutateAsync({
        id: panel.id,
        name: trimmed,
        configurationId: customConfigurationId,
        assays: panel.assays,
      });
      setIsCustomDraftDirty(false);
      setCustomNameInput(trimmed);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update panel name';
      toast({ title: 'Rename failed', description: message, status: 'error' });
      setCustomNameInput(panel.name);
      setCustomNameError(null);
    }
  }, [
    allowGeneTemplateEdits,
    commissionMutation,
    customConfigurationId,
    customNameInput,
    ensureCustomPanel,
    isCustomPanelActive,
    selectedExistingPanel,
    selection,
    toast,
    validateCustomName,
  ]);

  const applySelectionChanges = useCallback(
    (updates: Array<{ assay: string; nextSelected: boolean }>) => {
      const panel = selectedExistingPanel;
      if (!panel || disableInteractions) {
        return;
      }
      const allowedUpdates = updates.filter(({ assay }) => !isAssaySelectionBlocked(assay));
      if (allowedUpdates.length === 0) {
        return;
      }
      const currentSet = optimisticAssays ?? new Set(panel.assays);
      const baseVersion = getSelectionVersion(panel);
      const addAssays: string[] = [];
      const removeAssays: string[] = [];
      const nextSet = new Set(currentSet);

      allowedUpdates.forEach(({ assay, nextSelected }) => {
        if (assay === 'AY1') {
          return;
        }
        const alreadySelected = currentSet.has(assay);
        if (nextSelected && !alreadySelected) {
          addAssays.push(assay);
          nextSet.add(assay);
        }
        if (!nextSelected && alreadySelected) {
          removeAssays.push(assay);
          nextSet.delete(assay);
        }
      });

      if (addAssays.length === 0 && removeAssays.length === 0) {
        return;
      }

      setOptimisticAssays(nextSet);
      void selectionMutation
        .mutateAsync({
          panelId: panel.id,
          body: {
            baseSelectionVersion: baseVersion,
            addAssays,
            removeAssays,
          },
        })
        .catch(error => {
          const message = error instanceof Error ? error.message : 'Failed to update gene selection';
          toast({ description: message, status: 'error' });
          setOptimisticAssays(new Set(currentSet));
        });
    },
    [
      disableInteractions,
      getSelectionVersion,
      isAssaySelectionBlocked,
      optimisticAssays,
      selectedExistingPanel,
      selectionMutation,
      toast,
    ],
  );

  const [clipboardAction, setClipboardAction] = useState<'copy' | 'paste'>('copy');
  const [clipboardBusy, setClipboardBusy] = useState(false);

  const refreshSelectionClipboardAction = useCallback(async () => {
    if (!canPasteSelectionClipboard) {
      setClipboardAction('copy');
      return;
    }

    try {
      const text = await navigator.clipboard.readText();
      const payload = parseGenePanelSelectionClipboardV1(text);
      setClipboardAction(payload ? 'paste' : 'copy');
    } catch {
      setClipboardAction('copy');
    }
  }, [canPasteSelectionClipboard]);

  const handleCopyOrPasteSelections = useCallback(async () => {
    if (!canCopySelectionsToAnotherWorkbook) {
      return;
    }
    if (!assayList || allAssays.length === 0) {
      toast({ title: 'Assays not loaded yet', status: 'error' });
      return;
    }

    const copySelections = async () => {
      const payload: GenePanelSelectionClipboardV1 = {
        type: genePanelSelectionClipboardType,
        rows: allAssays.map(assay => ({ assay, selected: selectedAssaySet.has(assay) })),
      };
      await navigator.clipboard.writeText(JSON.stringify(payload));
      toast({ title: 'Selections copied', status: 'success' });
      if (canPasteSelectionClipboard) {
        setClipboardAction('paste');
      }
    };

    setClipboardBusy(true);
    try {
      if (!canPasteSelectionClipboard) {
        await copySelections();
        return;
      }

      let clipboardText: string | null = null;
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch {
        clipboardText = null;
      }
      const clipboardPayload = clipboardText ? parseGenePanelSelectionClipboardV1(clipboardText) : null;
      if (!clipboardPayload) {
        await copySelections();
        return;
      }

      const panel = selectedExistingPanel;
      if (!panel) {
        toast({ title: 'Select an editable panel first', status: 'warning' });
        return;
      }

      const clipboardAssays = clipboardPayload.rows.map(row => row.assay);
      const clipboardAssaySet = new Set(clipboardAssays);
      if (clipboardAssaySet.size !== clipboardAssays.length) {
        toast({ title: 'Paste disabled', description: 'Clipboard contains duplicate assays.', status: 'warning' });
        return;
      }

      const expectedAssaySet = new Set(allAssays);
      const missing = allAssays.filter(assay => !clipboardAssaySet.has(assay));
      const extra = clipboardAssays.filter(assay => !expectedAssaySet.has(assay));
      if (missing.length > 0 || extra.length > 0) {
        const missingLabel = missing.length > 0 ? `missing ${missing.length}` : null;
        const extraLabel = extra.length > 0 ? `extra ${extra.length}` : null;
        const summary = [missingLabel, extraLabel].filter(Boolean).join(', ');
        toast({
          title: 'Paste disabled',
          description: `Clipboard assay set does not match this page (${summary}).`,
          status: 'error',
        });
        return;
      }

      const desiredSelected = new Set(clipboardPayload.rows.filter(row => row.selected).map(row => row.assay));
      desiredSelected.add('AY1');

      const blockedSoftLaunchChanges = allAssays.filter(
        assay => isAssaySelectionBlocked(assay) && selectedAssaySet.has(assay) !== desiredSelected.has(assay),
      );
      if (blockedSoftLaunchChanges.length > 0) {
        toast({
          title: 'Paste disabled',
          description: `Only Resistomap team members can change soft-launched assays: ${blockedSoftLaunchChanges
            .slice(0, 6)
            .join(', ')}${blockedSoftLaunchChanges.length > 6 ? ', …' : ''}`,
          status: 'error',
        });
        return;
      }

      const wouldAddDeprecated = Array.from(desiredSelected).filter(assay => {
        if (assay === 'AY1') {
          return false;
        }
        if (selectedAssaySet.has(assay)) {
          return false;
        }
        return Boolean(assayByAy.get(assay)?.deprecated);
      });
      if (wouldAddDeprecated.length > 0) {
        toast({
          title: 'Paste disabled',
          description: `Cannot add deprecated assays: ${wouldAddDeprecated.slice(0, 6).join(', ')}${
            wouldAddDeprecated.length > 6 ? ', …' : ''
          }`,
          status: 'error',
        });
        return;
      }

      const addAssays = allAssays.filter(
        assay => assay !== 'AY1' && desiredSelected.has(assay) && !selectedAssaySet.has(assay),
      );
      const removeAssays = Array.from(selectedAssaySet).filter(assay => assay !== 'AY1' && !desiredSelected.has(assay));

      if (addAssays.length === 0 && removeAssays.length === 0) {
        toast({ title: 'No changes to apply', status: 'info' });
        return;
      }

      if (!hasOnlyMandatoryAssay) {
        const confirmed = window.confirm('This will overwrite the current assay selections for this panel. Continue?');
        if (!confirmed) {
          return;
        }
      }

      const baseVersion = getSelectionVersion(panel);
      const currentSet = optimisticAssays ?? new Set(panel.assays);
      setOptimisticAssays(new Set(desiredSelected));
      try {
        await selectionMutation.mutateAsync({
          panelId: panel.id,
          body: {
            baseSelectionVersion: baseVersion,
            addAssays,
            removeAssays,
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to apply selections';
        toast({ title: 'Paste failed', description: message, status: 'error' });
        setOptimisticAssays(new Set(currentSet));
        return;
      }
      setClipboardAction('paste');
      toast({ title: 'Selections pasted', status: 'success' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Clipboard operation failed';
      toast({ title: 'Clipboard operation failed', description: message, status: 'error' });
    } finally {
      setClipboardBusy(false);
    }
  }, [
    allAssays,
    assayByAy,
    assayList,
    canCopySelectionsToAnotherWorkbook,
    canPasteSelectionClipboard,
    getSelectionVersion,
    hasOnlyMandatoryAssay,
    isAssaySelectionBlocked,
    optimisticAssays,
    selectedAssaySet,
    selectedExistingPanel,
    selectionMutation,
    toast,
  ]);

  const copyAssayRowsToClipboard = useCallback(
    async (
      rowsToCopy: readonly GeneSelectionGridRow[],
      columns: readonly GeneSelectionClipboardColumn[],
      successTitle: string,
      emptyTitle: string,
    ) => {
      if (rowsToCopy.length === 0) {
        toast({ title: emptyTitle, status: 'info' });
        return;
      }

      setClipboardBusy(true);
      try {
        await navigator.clipboard.writeText(buildGeneSelectionTsv(rowsToCopy, columns));
        toast({ title: successTitle, status: 'success' });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Clipboard operation failed';
        toast({ title: 'Clipboard operation failed', description: message, status: 'error' });
      } finally {
        setClipboardBusy(false);
      }
    },
    [toast],
  );

  const handleCopyAllAssays = useCallback(async () => {
    await copyAssayRowsToClipboard(
      rowData,
      isResistomapAdmin ? assayClipboardColumns : allAssayClipboardColumnsWithoutPrimers,
      'All assays copied',
      'No assays to copy',
    );
  }, [copyAssayRowsToClipboard, isResistomapAdmin, rowData]);

  const handleCopySelectedAssays = useCallback(async () => {
    await copyAssayRowsToClipboard(
      selectedRows,
      canCopySelectedAssayPrimers ? assayClipboardColumns : allAssayClipboardColumnsWithoutPrimers,
      'Selected assays copied',
      'No selected assays to copy',
    );
  }, [canCopySelectedAssayPrimers, copyAssayRowsToClipboard, selectedRows]);

  const handleToggleAssay = useCallback(
    (assay: string) => {
      const panel = selectedExistingPanel;
      if (!panel || disableInteractions || assay === 'AY1' || isAssaySelectionBlocked(assay)) {
        return;
      }
      const currentSet = optimisticAssays ?? new Set(panel.assays);
      const alreadySelected = currentSet.has(assay);
      applySelectionChanges([{ assay, nextSelected: !alreadySelected }]);
    },
    [applySelectionChanges, disableInteractions, isAssaySelectionBlocked, optimisticAssays, selectedExistingPanel],
  );

  const handleSelectionCellValueChanged = useCallback(
    (event: CellValueChangedEvent<GeneSelectionGridRow, boolean>) => {
      if (event.colDef.colId !== 'selected') {
        return;
      }
      const parsed = parseSelectionCellValue(event.newValue);
      if (parsed == null) {
        event.node.setDataValue('selected', event.oldValue);
        return;
      }
      if (isAssaySelectionBlocked(event.data.assay)) {
        event.node.setDataValue('selected', event.oldValue);
        return;
      }
      if (clipboardPasteChangesRef.current) {
        clipboardPasteChangesRef.current.set(event.data.assay, parsed);
        return;
      }
      applySelectionChanges([{ assay: event.data.assay, nextSelected: parsed }]);
    },
    [applySelectionChanges, isAssaySelectionBlocked],
  );

  const handlePasteStart = useCallback(
    (event: PasteStartEvent<GeneSelectionGridRow>) => {
      if (disableInteractions || isReadOnlySelection) {
        clipboardPasteChangesRef.current = null;
        return;
      }
      const ranges = event.api.getCellRanges();
      const includesSelectionColumn =
        ranges?.some(range => range.columns.some(column => column.getColId() === 'selected')) ?? false;
      clipboardPasteChangesRef.current = includesSelectionColumn ? new Map<string, boolean>() : null;
    },
    [disableInteractions, isReadOnlySelection],
  );

  const handlePasteEnd = useCallback(() => {
    const changes = clipboardPasteChangesRef.current;
    clipboardPasteChangesRef.current = null;
    if (!changes || changes.size === 0) {
      return;
    }
    const updates = Array.from(changes.entries()).map(([assay, nextSelected]) => ({ assay, nextSelected }));
    applySelectionChanges(updates);
  }, [applySelectionChanges]);

  const handleSelectionChange = useCallback(
    async (value: SelectionValue) => {
      if (!allowGeneTemplateEdits) {
        return;
      }
      if (isWorkbookReadOnly && !value.startsWith('existing:')) {
        return;
      }
      setSelection(value);
      setIsCustomDraftDirty(false);
      setOptimisticAssays(null);
      setCustomNameError(null);

      if (value.startsWith('custom:')) {
        const candidate = getSelectionPayload(value);
        if (isGenePanelConfigurationId(candidate)) {
          setCustomConfigurationId(candidate);
        }
        setCustomModePanelId(null);
        setCustomNameInput('');
        if (onLinkPanel) {
          void onLinkPanel(null);
        }
        return;
      }

      if (value.startsWith('default:')) {
        const key = getSelectionPayload(value);
        const definition = defaultGenePanelDefinitions.find(def => def.key === key);
        if (definition) {
          await commissionDefaultPanel(definition);
        }
        return;
      }

      if (value.startsWith('existing:')) {
        const id = Number(getSelectionPayload(value));
        if (!Number.isFinite(id)) {
          return;
        }
        const panel = panelsById.get(id);
        if (panel && findDefaultGenePanelByAssays(defaultGenePanelDefinitions, panel.assays) == null) {
          setCustomModePanelId(panel.id);
          setCustomNameInput(panel.name);
          const inferred = findConfigurationIdByCounts(panel.genes, panel.samples);
          if (inferred) {
            setCustomConfigurationId(inferred);
          }
          setCustomNameError(null);
        } else {
          setCustomModePanelId(null);
        }
      }
    },
    [
      allowGeneTemplateEdits,
      commissionDefaultPanel,
      defaultGenePanelDefinitions,
      isWorkbookReadOnly,
      onLinkPanel,
      panelsById,
    ],
  );

  const gridRef = useRef<AgGridReact<GeneSelectionGridRow> | null>(null);
  const [gridSearchText, setGridSearchText] = useState('');
  const [gridDisplayCounts, setGridDisplayCounts] = useState(() => ({
    displayed: visibleRows.length,
    total: visibleRows.length,
    filtered: false,
  }));
  const refreshGridDisplayCounts = useCallback(() => {
    const api = gridRef.current?.api;
    const displayed = api?.getDisplayedRowCount() ?? visibleRows.length;
    const filtered = Boolean(gridSearchText.trim()) || (api?.isAnyFilterPresent() ?? false);
    setGridDisplayCounts({
      displayed,
      total: visibleRows.length,
      filtered,
    });
  }, [gridSearchText, visibleRows.length]);
  useEffect(() => {
    refreshGridDisplayCounts();
  }, [gridSearchText, refreshGridDisplayCounts]);
  const handleGridReady = useCallback(
    (_event: GridReadyEvent<GeneSelectionGridRow>) => {
      refreshGridDisplayCounts();
    },
    [refreshGridDisplayCounts],
  );
  const handleGridFilterChanged = useCallback(
    (_event: FilterChangedEvent<GeneSelectionGridRow>) => {
      refreshGridDisplayCounts();
    },
    [refreshGridDisplayCounts],
  );

  const gridContext = useMemo<SelectionCellContext>(
    () => ({
      onToggle: handleToggleAssay,
      isReadOnlySelection,
      disableInteractions,
      canSelectSoftLaunchedAssays,
    }),
    [canSelectSoftLaunchedAssays, disableInteractions, handleToggleAssay, isReadOnlySelection],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    api.refreshCells({ columns: ['selected'], force: true, suppressFlash: true });
  }, [canSelectSoftLaunchedAssays, disableInteractions, isReadOnlySelection]);
  const rowSelection = useMemo<RowSelectionOptions<GeneSelectionGridRow>>(
    () => ({
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    }),
    [],
  );

  const gridColumnDefs = useMemo<Array<ColDef<GeneSelectionGridRow>>>(
    () => [
      {
        headerName: 'Select',
        field: 'selected',
        colId: 'selected',
        width: 96,
        minWidth: 96,
        maxWidth: 96,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params: ValueFormatterParams<GeneSelectionGridRow, boolean>) =>
            params.value ? 'Selected' : 'Not selected',
        },
        sortable: true,
        comparator: (valueA, valueB) => Number(Boolean(valueA)) - Number(Boolean(valueB)),
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        suppressColumnsToolPanel: true,
        lockPosition: true,
        pinned: 'left',
        cellRenderer: SelectionCellRenderer,
        cellClass: 'gene-selection-cell gene-selection-cell-checkbox',
        editable: ({ data }) => (data ? !isSelectionCellDisabled(data, gridContext) : false),
        valueGetter: ({ data }) => data?.selected ?? false,
        valueSetter: (params: SelectionValueSetterParams) => {
          const parsed = parseSelectionCellValue(params.newValue);
          if (parsed == null || !params.data) {
            return false;
          }
          if (isSelectionCellDisabled(params.data, gridContext)) {
            return false;
          }
          params.data.selected = parsed;
          return params.data.selected !== params.oldValue;
        },
      },
      { headerName: 'Assay', field: 'assay', minWidth: 92, width: 92, maxWidth: 90 },
      { headerName: 'Assay target', field: 'gene', minWidth: 100, width: 130 },
      {
        headerName: 'Target Group',
        field: 'target',
        minWidth: 100,
        width: 200,
        maxWidth: 200,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'ARG 1.0 Panel',
        field: 'arg10',
        hide: true,
        ...booleanColumnDef,
        minWidth: 80,
        width: 100,
        maxWidth: 100,
      },
      {
        headerName: 'ARG 2.0 Panel',
        field: 'arg20',
        hide: true,
        ...booleanColumnDef,
        minWidth: 80,
        width: 100,
        maxWidth: 100,
      },
      {
        headerName: 'AY std. curve',
        field: 'assayStandardCurve',
        ...booleanColumnDef,
        minWidth: 120,
        width: 130,
        maxWidth: 140,
      },
      { headerName: 'ARG 2.1', field: 'arg21', ...booleanColumnDef, minWidth: 80, width: 105, maxWidth: 110 },
      {
        headerName: 'One Health 96',
        field: 'oneHealth96',
        ...booleanColumnDef,
        minWidth: 80,
        width: 150,
        maxWidth: 150,
      },
      { headerName: forwardPrimerColumnHeader, field: 'forwardPrimer', minWidth: 170 },
      { headerName: reversePrimerColumnHeader, field: 'reversePrimer', minWidth: 170 },
    ],
    [booleanColumnDef, gridContext],
  );

  if (!organisationIdParam && !isEmbedded) {
    return (
      <Box px={8} py={12} maxW="960px" margin="0 auto">
        <Heading size="lg" mb={4}>
          Gene panels
        </Heading>
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            Provide a lab workbook id in the URL (e.g.
            /lab/00000000-0000-0000-0000-000000000000/gene-template?mode=genes).
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!hasOrganisationId || invalidOrganisationId || organisationId == null) {
    return (
      <Box px={8} py={12} maxW="960px" margin="0 auto">
        <Heading size="lg" mb={4}>
          Gene panels
        </Heading>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            Provide a valid lab workbook id (UUID), for example
            /lab/00000000-0000-0000-0000-000000000000/gene-template?mode=genes.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (panelsError || assaysError || defaultGenePanelDefinitionsError) {
    const errorMessage =
      panelsError?.message ??
      assaysError?.message ??
      defaultGenePanelDefinitionsError?.message ??
      'Failed to load gene panel data';
    return (
      <Box px={8} py={12} maxW="960px" margin="0 auto">
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (panelsLoading || assaysLoading) {
    return (
      <Flex px={8} py={12} align="center" justify="center">
        <Spinner size="lg" />
      </Flex>
    );
  }

  // Title header shows only the page title now; no organisation label needed here.
  const showForm = isCustomPanelActive || Boolean(selectedExistingPanel) || Boolean(selectedDefaultDefinition);
  const showGrid = Boolean(selectedExistingPanel || selectedDefaultDefinition);
  const stateBadgeLabel = selectedPanelState
    ? formatGenePanelStateLabel(selectedPanelState) + (showMatchesDefaultInBadge ? ' (matches default)' : '')
    : 'Unselected';
  const stateBadgeColor = selectedPanelState ? genePanelStateColorScheme(selectedPanelState) : 'gray';
  const isExistingCustomPanelSelected = Boolean(
    selectedExistingPanel && customModePanelId != null && selectedExistingPanel.id === customModePanelId,
  );
  const customControlsDisabled = Boolean(
    selectedExistingPanel &&
      selectedExistingPanel.id === customModePanelId &&
      selectedExistingPanel.state === GenePanelState.LOCKED,
  );
  const canShowStartButtons =
    isExistingCustomPanelSelected && !customControlsDisabled && !disableInteractions && hasOnlyMandatoryAssay;
  const showStart96 = canShowStartButtons && customConfigurationId === '96:18';
  const showStart21 = canShowStartButtons && customConfigurationId === '384:4';
  const containerPadding = isEmbedded ? 0 : theme.spacing[4];
  const containerMargin = isEmbedded ? '0' : '0 auto';
  const containerMaxWidth = isEmbedded ? 'none' : '1600px';

  return (
    <Box px={containerPadding} py={containerPadding} maxW={containerMaxWidth} w="100%" margin={containerMargin}>
      <VStack align="stretch" spacing={6}>
        <Text color="gray.600">
          Choose a default panel, review existing panels, or configure a custom selection tailored to the SmartChip
          layout.
        </Text>

        <FormControl maxW="720px">
          <FormLabel>Panel selection</FormLabel>
          <HStack spacing={3} align="flex-end">
            <Select
              value={selection}
              onChange={event => {
                void handleSelectionChange(event.target.value);
              }}
              isDisabled={isLinkingPanel || isWorkbookReadOnly || !allowGeneTemplateEdits}
            >
              {selectionOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {showStart96 && (
              <Button
                variant="outline"
                alignSelf="flex-end"
                colorScheme="blue"
                minW="210px"
                onClick={() => {
                  handleApplyDefaultTemplate('ONE_HEALTH_96');
                }}
              >
                Start with One Health panel
              </Button>
            )}
            {showStart21 && (
              <Button
                variant="outline"
                alignSelf="flex-end"
                colorScheme="blue"
                minW="210px"
                onClick={() => {
                  handleApplyDefaultTemplate('ARG_2_1');
                }}
              >
                Start with ARG 2.1 panel
              </Button>
            )}
          </HStack>
        </FormControl>

        {showForm && (
          <Flex gap={4} wrap="wrap">
            <FormControl maxW="330px" isRequired={isCustomPanelActive} isInvalid={Boolean(customNameError)}>
              <FormLabel>Panel name</FormLabel>
              {isCustomPanelActive ? (
                <Input
                  value={customNameInput}
                  onChange={event => {
                    setCustomNameError(null);
                    setIsCustomDraftDirty(true);
                    setCustomNameInput(event.target.value);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    void handleCustomNameBlur();
                  }}
                  placeholder="Enter panel name"
                  isDisabled={
                    !allowGeneTemplateEdits || disableInteractions || customControlsDisabled || isCommissioning
                  }
                />
              ) : (
                <Input value={currentPanelName} isReadOnly pointerEvents="none" />
              )}
              {isCustomPanelActive && customNameError && <FormErrorMessage>{customNameError}</FormErrorMessage>}
            </FormControl>
          </Flex>
        )}

        {selectedExistingPanel && (
          <Box>
            <Text fontWeight="semibold">Panel details</Text>
            <HStack spacing={4} mt={2} wrap="wrap">
              {currentConfiguration && (
                <Badge colorScheme="purple">
                  {currentConfiguration.genes} assays / {currentConfiguration.samples} samples
                </Badge>
              )}
              {expectedGeneCount != null && (
                <Badge colorScheme={selectionBadgeColorScheme}>{selectionBadgeLabel}</Badge>
              )}
              <Badge colorScheme={stateBadgeColor}>{stateBadgeLabel}</Badge>
            </HStack>
          </Box>
        )}
      </VStack>
      <br />

      {showGrid && (
        <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
          <Box px={4} py={3} borderBottomWidth="1px" bg="gray.50">
            <Flex gap={3} justify="space-between" align="center" wrap="wrap">
              <HStack spacing={3} flex="1 1 360px" minW="280px" wrap="wrap">
                <Text fontWeight="semibold" whiteSpace="nowrap">
                  Assay selection
                </Text>
                <Input
                  value={gridSearchText}
                  onChange={event => {
                    setGridSearchText(event.target.value);
                  }}
                  placeholder="Search genes or assays"
                  aria-label="Search genes or assays"
                  size="sm"
                  maxW="280px"
                  bg="white"
                />
                {gridSearchText.trim() ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setGridSearchText('');
                    }}
                    whiteSpace="nowrap"
                  >
                    Clear
                  </Button>
                ) : null}
              </HStack>
              <HStack spacing={3} wrap="wrap" justify="flex-end">
                {canCopySelectionsToAnotherWorkbook ? (
                  <Button
                    size="sm"
                    variant="outline"
                    isDisabled={clipboardBusy || allAssays.length === 0}
                    onClick={() => {
                      void handleCopyOrPasteSelections();
                    }}
                    onMouseEnter={() => {
                      void refreshSelectionClipboardAction();
                    }}
                    onFocus={() => {
                      void refreshSelectionClipboardAction();
                    }}
                    whiteSpace="nowrap"
                  >
                    {clipboardAction === 'paste' && canPasteSelectionClipboard
                      ? 'Paste from Another Workbook'
                      : 'Copy to Another Workbook'}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={clipboardBusy || rowData.length === 0}
                  onClick={() => {
                    void handleCopyAllAssays();
                  }}
                  whiteSpace="nowrap"
                >
                  Copy All Assays
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={clipboardBusy || selectedRows.length === 0}
                  onClick={() => {
                    void handleCopySelectedAssays();
                  }}
                  whiteSpace="nowrap"
                >
                  Copy Selected Assays
                </Button>
                <Text fontSize="sm" color="gray.600">
                  {gridDisplayCounts.filtered
                    ? `Displaying ${gridDisplayCounts.displayed}/${gridDisplayCounts.total} assays`
                    : `Displaying ${gridDisplayCounts.total} assays`}
                </Text>
              </HStack>
            </Flex>
          </Box>
          <Box>
            <div className="gene-selection-grid" style={{ width: '100%', height: 600 }}>
              <AgGridReact<GeneSelectionGridRow>
                ref={gridRef}
                rowData={rowData}
                columnDefs={gridColumnDefs}
                defaultColDef={defaultColDef}
                theme={themeQuartz}
                animateRows
                domLayout="normal"
                context={gridContext}
                rowSelection={rowSelection}
                quickFilterText={gridSearchText}
                rowHeight={25}
                headerHeight={36}
                enableCellTextSelection
                cellSelection
                suppressMenuHide
                suppressContextMenu
                undoRedoCellEditing
                undoRedoCellEditingLimit={1000}
                onCellValueChanged={handleSelectionCellValueChanged}
                onFilterChanged={handleGridFilterChanged}
                onGridReady={handleGridReady}
                onPasteStart={handlePasteStart}
                onPasteEnd={handlePasteEnd}
                // Keep row identity stable so scroll position is preserved on data refresh/focus
                getRowId={(rowParams: { data: GeneSelectionGridRow }) => rowParams.data.assay}
              />
            </div>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function GeneSelectionView() {
  const params = useParams<{ organisationId?: string }>();
  const organisationIdParam = params.organisationId ?? undefined;
  const parsedOrganisationId = organisationIdParam != null ? Number(organisationIdParam) : undefined;
  const organisationId =
    parsedOrganisationId != null && Number.isFinite(parsedOrganisationId) ? parsedOrganisationId : undefined;
  const invalidOrganisationId = organisationIdParam != null && organisationId == null;

  return (
    <GeneSelectionSheet
      organisationId={organisationId}
      organisationIdParam={organisationIdParam}
      invalidOrganisationId={invalidOrganisationId}
    />
  );
}
